import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Play,
  Pause,
  Download,
  Eye,
  Trash2,
  Search,
  ArrowRight,
  User,
  FileAudio,
  Languages
} from 'lucide-react';
import { 
  translationApi, 
  TranslationJob, 
} from '../../services/audioApi';
import { useProject } from '../../hooks/useProject';
import TranslationJobDetail from '../../components/Audio/TranslationJobDetail.tsx';
import { formatDistanceToNow } from 'date-fns';
import Pagination, { paginate } from '@/components/Common/Pagination';
import ViewToggle, { ViewMode } from '@/components/Common/ViewToggle';

const ITEMS_PER_PAGE = 10;

const TranslationJobs: React.FC = () => {
  const { selectedProject } = useProject();
  const [jobs, setJobs] = useState<TranslationJob[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<TranslationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [playingJob, setPlayingJob] = useState<string | null>(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchJobs();
  }, []);

  // Separate effect for polling that doesn't depend on jobs to avoid loops
  // useEffect(() => {
  //   const setupPolling = () => {
  //     const hasProcessingJobs = jobs.some(job => job.status === 'processing');
  //     const pollInterval = hasProcessingJobs ? 10000 : 10000; // 3s for processing, 10s otherwise
      
  //     return setInterval(() => {
  //       fetchJobs(true); // Silent refresh
  //     }, pollInterval);
  //   };
    
  //   const interval = setupPolling();
  //   return () => clearInterval(interval);
  // }, [jobs.length, jobs.filter(job => job.status === 'processing').length]);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  const fetchJobs = async (silent: boolean = false) => {
    try {
      if (!silent) {
        setRefreshing(true);
      }
      
      const response = await translationApi.getTranslationJobs({ limit: 50 });
      
      // Always update jobs to ensure UI reflects latest status
      const newJobs = response.data.jobs;
      console.log('New jobs:', newJobs);
      console.log('Old jobs:', jobs);
      // Check for meaningful changes (status, progress, steps)
      const hasStatusChanges = newJobs.some((newJob: TranslationJob, index: number) => {
        const oldJob = jobs[index];
        return !oldJob || 
               newJob.status !== oldJob.status || 
               newJob.progress !== oldJob.progress ||
               newJob.currentStep !== oldJob.currentStep ||
               JSON.stringify(newJob.steps) !== JSON.stringify(oldJob.steps);
      });
      
      if (hasStatusChanges || jobs.length !== newJobs.length || jobs.length === 0) {
        setJobs(newJobs);
        setLastRefresh(new Date());
        console.log('Jobs updated:', newJobs.length, 'jobs');
      }
      
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch translation jobs:', error);
      if (!silent) {
        setError('Failed to fetch translation jobs');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.jobId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.result?.sourceLanguage && job.result.sourceLanguage.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.result?.targetLanguage && job.result.targetLanguage.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredJobs(filtered);
  };

  const cancelJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to cancel this job?')) return;

    try {
      await translationApi.cancelTranslation(jobId);
      setJobs(prev => prev.filter(job => job.jobId !== jobId));
    } catch (error: any) {
      console.error('Failed to cancel job:', error);
      setError('Failed to cancel job');
    }
  };

  const retryJob = async (jobId: string) => {
    try {
      await translationApi.retryJob(jobId);
      await fetchJobs(true);
    } catch (error: any) {
      console.error('Failed to retry job:', error);
      setError('Failed to retry job');
    }
  };

  const togglePlayback = (jobId: string, audioUrl: string) => {
    if (playingJob === jobId) {
      setPlayingJob(null);
    } else {
      setPlayingJob(jobId);
      const audio = new Audio(audioUrl);
      audio.onended = () => setPlayingJob(null);
      audio.play();
    }
  };

  const getStatusBadge = (status: string, progress?: number) => {
    const badges = {
      pending: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      ),
      processing: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
          Processing {progress ? `${progress}%` : ''}
        </span>
      ),
      completed: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      ),
      failed: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      ),
    };
    return badges[status as keyof typeof badges] || null;
  };

  const getJobStats = () => {
    const total = jobs.length;
    const completed = jobs.filter(job => job.status === 'completed').length;
    const processing = jobs.filter(job => job.status === 'processing').length;
    const failed = jobs.filter(job => job.status === 'failed').length;
    const pending = jobs.filter(job => job.status === 'pending').length;

    return { total, completed, processing, failed, pending };
  };

  const stats = getJobStats();
  const paginatedJobs = paginate(filteredJobs, page, ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading translation jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Context Indicator */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Project context:</span>{' '}
            {selectedProject ? (
              <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                {selectedProject.name}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                None (org-wide)
              </span>
            )}
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Translation Jobs</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Monitor and manage your audio translation jobs
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <FileAudio className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Processing</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.processing}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Clock className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{stats.pending}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by job ID or language..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button
              onClick={() => fetchJobs(false)}
              disabled={refreshing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 dark:hover:text-red-300"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* Auto-refresh indicator */}
        <div className="mb-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
            {refreshing && (
              <div className="flex items-center">
                <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                <span>Updating...</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Auto-refresh active</span>
          </div>
        </div>

        {/* Jobs List */}
        <div className="flex items-center justify-end mb-3">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Languages className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No translation jobs found</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Start your first translation to see jobs here'
                }
              </p>
            </div>
          ) : viewMode === 'card' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {paginatedJobs.map((job) => (
                <div key={job.jobId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Job #{job.jobId.slice(-8)}</h3>
                    {getStatusBadge(job.status, job.progress)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                  </p>
                  {job.result && (
                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 mb-3">
                      <Languages className="w-3.5 h-3.5 mr-1" />
                      <span>{job.result.sourceLanguage.toUpperCase()}</span>
                      <ArrowRight className="w-3.5 h-3.5 mx-1.5" />
                      <span>{job.result.targetLanguage.toUpperCase()}</span>
                    </div>
                  )}
                  {job.error && (
                    <p className="text-xs text-red-600 dark:text-red-400 mb-3 line-clamp-2">{job.error}</p>
                  )}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {job.result?.outputAudioUrl && (
                      <>
                        <button
                          onClick={() => togglePlayback(job.jobId, job.result!.outputAudioUrl)}
                          className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                          title="Play audio"
                        >
                          {playingJob === job.jobId ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </button>
                        <a
                          href={job.result.outputAudioUrl}
                          download
                          className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                          title="Download audio"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </>
                    )}
                    {job.status === 'failed' && (
                      <button
                        onClick={() => retryJob(job.jobId)}
                        className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                        title="Retry from last completed step"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => { setSelectedJobId(job.jobId); setShowJobDetail(true); }}
                      className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="View details"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => cancelJob(job.jobId)}
                      className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                      title="Delete job"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedJobs.map((job) => (
                <div key={job.jobId} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          Job #{job.jobId.slice(-8)}
                        </h3>
                        {getStatusBadge(job.status, job.progress)}
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      
                      {job.currentStep && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{job.currentStep}</p>
                      )}

                      {/* Step Progress Indicators */}
                      {job.steps && job.steps.length > 0 && (
                        <div className="flex items-center space-x-2 mb-2">
                          {job.steps.map((step) => (
                            <div
                              key={step.id}
                              className={`w-3 h-3 rounded-full ${
                                step.status === 'completed' ? 'bg-green-500' :
                                step.status === 'processing' ? 'bg-blue-500 animate-pulse' :
                                step.status === 'failed' ? 'bg-red-500' :
                                'bg-gray-300'
                              }`}
                              title={`${step.name}: ${step.status}`}
                            />
                          ))}
                        </div>
                      )}

                      {job.result && (
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center">
                            <Languages className="w-4 h-4 mr-1" />
                            <span>{job.result.sourceLanguage.toUpperCase()}</span>
                            <ArrowRight className="w-4 h-4 mx-2" />
                            <span>{job.result.targetLanguage.toUpperCase()}</span>
                          </div>
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            <span>{job.result.voiceName}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{(job.result.processingTime / 1000).toFixed(1)}s</span>
                          </div>
                        </div>
                      )}

                      {job.error && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded text-sm text-red-600 dark:text-red-400">
                          {job.error}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {job.result?.outputAudioUrl && (
                        <>
                          <button
                            onClick={() => togglePlayback(job.jobId, job.result!.outputAudioUrl)}
                            className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                            title="Play audio"
                          >
                            {playingJob === job.jobId ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                          <a
                            href={job.result.outputAudioUrl}
                            download
                            className="p-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                            title="Download audio"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </>
                      )}
                      {job.status === 'failed' && (
                        <button
                          onClick={() => retryJob(job.jobId)}
                          className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                          title="Retry from last completed step"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedJobId(job.jobId);
                          setShowJobDetail(true);
                        }}
                        className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => cancelJob(job.jobId)}
                        className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                        title="Delete job"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(filteredJobs.length / ITEMS_PER_PAGE)}
          onPageChange={setPage}
          totalItems={filteredJobs.length}
          itemsPerPage={ITEMS_PER_PAGE}
        />

        {/* Enhanced Job Detail Modal */}
        {showJobDetail && selectedJobId && (
          <TranslationJobDetail
            jobId={selectedJobId}
            isOpen={showJobDetail}
            onClose={() => {
              setShowJobDetail(false);
              setSelectedJobId(null);
            }}
            onJobUpdate={(updatedJob: TranslationJob) => {
              // Update the job in the local state
              setJobs(prev => 
                prev.map(job => 
                  job.jobId === updatedJob.jobId ? updatedJob : job
                )
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TranslationJobs;
